import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Phone, Info } from 'lucide-react';

const EauChaudeSanitairePage = () => {
  return (
    <div className="EauChaudeSanitairePage bg-white">
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center py-24"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1576759310319-aa88751eca50)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D9A68]/95 to-[#2F5BA0]/85"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <p className="text-lg mb-3 font-semibold opacity-90">💧 Production d'eau chaude</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Eau Chaude Sanitaire</h1>
            <p className="text-xl md:text-2xl mb-6">Solutions économiques et écologiques pour votre eau chaude</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-[#2F5BA0] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#1e3a66] transition-all inline-flex items-center justify-center gap-2"
              >
                Nous contacter
              </Link>
              <a
                href="tel:0188617508"
                className="bg-white text-[#2D9A68] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone size={20} />
                01 88 61 75 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Comparatif rapide */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Quel chauffe-eau choisir ?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <a 
                href="#ballon-electrique" 
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer transform hover:scale-105"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">⚡ Électrique</h3>
                <p className="text-gray-600 mb-3">Simple et économique à l'achat</p>
                <p className="text-sm text-gray-500">Installation facile • Pas d'aides</p>
              </a>
              <a 
                href="#ballon-thermodynamique" 
                className="bg-gradient-to-br from-[#2D9A68] to-[#2F5BA0] p-6 rounded-xl shadow-lg text-white hover:shadow-2xl transition-all cursor-pointer transform hover:scale-105"
              >
                <h3 className="text-xl font-bold mb-2">🔥 Thermodynamique</h3>
                <p className="mb-3">Économies garanties • Meilleur compromis</p>
                <p className="text-sm opacity-90">Aides jusqu'à 1 300€</p>
              </a>
              <a 
                href="#ballon-solaire" 
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer transform hover:scale-105"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">☀️ Solaire</h3>
                <p className="text-gray-600 mb-3">Écologique • 70% d'eau chaude gratuite</p>
                <p className="text-sm text-gray-500">Aides jusqu'à 4 140€</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BALLON ÉLECTRIQUE */}
      <section id="ballon-electrique" className="py-16 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Titre */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ballon d'Eau Chaude Électrique</h2>
              <p className="text-xl text-gray-600">La solution classique et éprouvée</p>
            </div>

            {/* Info box - Pas d'aides */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-12 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="text-blue-600 mt-1 flex-shrink-0" size={24} />
                <div>
                  <p className="text-blue-900 font-semibold mb-2 text-lg">ℹ️ Information</p>
                  <p className="text-blue-800 text-lg">Le ballon électrique classique n'est pas éligible aux aides de l'État (MaPrimeRénov' ou CEE). Pour bénéficier d'aides, privilégiez le ballon thermodynamique ou solaire.</p>
                </div>
              </div>
            </div>

            {/* Qu'est-ce que */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Qu'est-ce qu'un ballon d'eau chaude électrique ?</h3>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le chauffe-eau électrique, aussi appelé cumulus ou ballon d'eau chaude, est l'équipement le plus courant dans les foyers français. Il fonctionne grâce à une résistance électrique immergée dans une cuve isolée qui chauffe l'eau progressivement, généralement pendant les heures creuses pour optimiser le coût.
              </p>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Simple, fiable et économique à l'achat, le ballon électrique reste une solution pratique pour les petits logements ou en remplacement d'urgence. Cependant, il consomme beaucoup d'électricité comparé aux solutions thermodynamiques ou solaires.
              </p>
            </div>

            {/* Comment ça fonctionne */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comment fonctionne un ballon électrique ?</h3>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le fonctionnement est simple et éprouvé :
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-4 text-lg ml-4">
                <li><strong>Alimentation :</strong> L'eau froide entre par le bas de la cuve</li>
                <li><strong>Chauffage :</strong> Une résistance électrique (stéatite ou blindée) chauffe l'eau à la température programmée (généralement 60-65°C)</li>
                <li><strong>Stratification :</strong> L'eau chaude, moins dense, monte naturellement vers le haut de la cuve</li>
                <li><strong>Distribution :</strong> L'eau chaude est puisée par le haut pour être distribuée aux robinets</li>
              </ol>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le thermostat maintient la température constante. L'isolation de la cuve limite les déperditions thermiques. Le temps de chauffe complet est de 6 à 8 heures.
              </p>
            </div>

            {/* Avantages et Inconvénients */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">✅ Avantages</h3>
                <div className="space-y-4">
                  {[
                    '💰 Prix d\'achat très abordable (300-800€)',
                    '🔧 Installation simple et rapide',
                    '⚙️ Fiabilité éprouvée (durée de vie 10-15 ans)',
                    '🛠️ Entretien minimal',
                    '📏 Nombreuses capacités disponibles (50-300L)',
                    '🏠 Convient à tous types de logements',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="text-[#2D9A68] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">⚠️ Inconvénients</h3>
                <div className="space-y-4">
                  {[
                    '⚡ Consommation électrique élevée (3x plus qu\'un thermodynamique)',
                    '💸 Coût d\'usage important (400-600€/an)',
                    '❌ Pas d\'aides de l\'État (MaPrimeRénov\', CEE)',
                    '♻️ Impact écologique important',
                    '📉 Rendement faible (COP = 1)',
                    '🔌 Dépendant du tarif électrique',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-red-500 mt-1 flex-shrink-0">✕</span>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pour quels logements */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pour quel type de logement ?</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 text-lg">
                <li><strong>Petits logements</strong> : Studios, T1, T2 (1-2 personnes)</li>
                <li><strong>Remplacement d'urgence</strong> : Quand le ballon est en panne</li>
                <li><strong>Budget limité</strong> : Si pas de possibilité d'investir dans du thermodynamique</li>
                <li><strong>Pas d'espace</strong> : Quand impossible d'installer un ballon thermodynamique (besoin de 10m²)</li>
                <li>⚠️ Pour des économies, privilégiez le <a href="#ballon-thermodynamique" className="text-[#2D9A68] underline">ballon thermodynamique</a> ou <a href="#ballon-solaire" className="text-[#2D9A68] underline">solaire</a></li>
              </ul>
            </div>

            {/* Caractéristiques techniques */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Caractéristiques Techniques</h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <ul className="space-y-4">
                  {[
                    'Capacité : 50 à 300 litres (selon foyer)',
                    'Puissance : 1 200 à 3 000 W',
                    'Temps de chauffe : 6-8 heures',
                    'Température eau : 60-65°C',
                    'Classe énergétique : C à E',
                    'COP : 1 (1 kW électrique = 1 kW chaleur)',
                    'Durée de vie : 10-15 ans',
                    'Garantie cuve : 5 ans (ACI)',
                  ].map((spec, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <Check className="text-[#2F5BA0] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTA vers thermodynamique */}
            <div className="bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-3">💡 Vous voulez faire des économies ?</h3>
              <p className="text-lg mb-6">
                Le ballon thermodynamique consomme 3 fois moins et vous donne droit à 1 300€ d'aides !
              </p>
              <a
                href="#ballon-thermodynamique"
                className="inline-flex items-center gap-2 bg-white text-[#2D9A68] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all"
              >
                Découvrir le ballon thermodynamique <ArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="border-t-4 border-gray-200"></div>

      {/* BALLON THERMODYNAMIQUE */}
      <section id="ballon-thermodynamique" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Titre */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ballon Thermodynamique</h2>
              <p className="text-xl text-gray-600">Eau chaude ultra-économique - Divisez par 3 votre facture</p>
            </div>

            {/* Aides */}
            <div className="bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white rounded-2xl p-8 text-center mb-12 shadow-lg">
              <p className="text-lg mb-2 opacity-90">💰 Aides disponibles</p>
              <p className="text-3xl md:text-4xl font-bold">Jusqu'à 1 300€ (MaPrimeRénov' 1 200€ + CEE 100€)</p>
            </div>

            {/* Images */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                'https://www.climpac-aci.fr/wp-content/uploads/sites/8905/2020/07/schema-chauffe-eau-thermodynamique.jpg',
                'https://im.qccdn.fr/node/guide-d-achat-chauffe-eau-thermodynamiques-23561/thumbnail_1000x600px-122590.jpg',
                'https://eh-renov.fr/wp-content/uploads/2024/09/ballon_thermo_explicatif-1024x768.webp',
              ].map((img, index) => (
                <div key={index} className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all bg-white flex items-center justify-center" style={{minHeight: '300px'}}>
                  <img 
                    src={img} 
                    alt={`Ballon Thermodynamique - ${index + 1}`}
                    className="w-full h-auto max-h-96 object-contain p-4"
                  />
                </div>
              ))}
            </div>

            {/* Qu'est-ce que */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Qu'est-ce qu'un ballon thermodynamique ?</h3>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le chauffe-eau thermodynamique (CET) est un ballon d'eau chaude équipé d'une mini pompe à chaleur intégrée. Il capte les calories présentes dans l'air de votre pièce (buanderie, garage, cave) ou de l'air extérieur pour chauffer l'eau sanitaire. Cette technologie permet de diviser par 3 votre consommation d'électricité pour l'eau chaude par rapport à un ballon électrique traditionnel.
              </p>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Avec un COP moyen de 3, il produit 3 kW de chaleur pour seulement 1 kW d'électricité consommée. Une résistance électrique d'appoint prend le relais automatiquement en cas de besoin exceptionnel (grand froid, puisage important).
              </p>
            </div>

            {/* Comment ça fonctionne */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comment fonctionne un ballon thermodynamique ?</h3>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le fonctionnement repose sur le principe aérothermique :
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-4 text-lg ml-4">
                <li><strong>Captation de l'air :</strong> Un ventilateur aspire l'air ambiant de la pièce (ou extérieur via des gaines)</li>
                <li><strong>Récupération des calories :</strong> L'évaporateur extrait la chaleur de cet air</li>
                <li><strong>Compression :</strong> Le compresseur élève la température du fluide frigorigène</li>
                <li><strong>Transfert de chaleur :</strong> Le condenseur transfère la chaleur à l'eau du ballon</li>
              </ol>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le temps de chauffe est de 6 à 8 heures en mode économique. Un mode boost permet de chauffer plus rapidement si besoin. L'installation est simple : il suffit de le placer dans une pièce non chauffée (buanderie, garage, cellier) de minimum 10 m².
              </p>
            </div>

            {/* Avantages et Inconvénients */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">✅ Avantages</h3>
                <div className="space-y-4">
                  {[
                    '💰 Divisez par 3 votre facture d\'eau chaude',
                    '🔥 Aides jusqu\'à 1 300€',
                    '⚡ 70% d\'économies vs électrique',
                    '♻️ Écologique : utilise l\'air ambiant',
                    '⏱️ Rentable en 3-4 ans',
                    '🌡️ Mode boost intégré',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="text-[#2D9A68] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">⚠️ Inconvénients</h3>
                <div className="space-y-4">
                  {[
                    '📏 Nécessite espace 10m² minimum',
                    '🏠 Pièce non chauffée requise',
                    '🔇 Niveau sonore 35-45 dB',
                    '💶 Coût initial plus élevé qu\'électrique',
                    '❄️ Performance réduite par grand froid',
                    '🔧 Entretien tous les 2-3 ans',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-red-500 mt-1 flex-shrink-0">✕</span>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pour quels logements */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pour quel type de logement ?</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 text-lg">
                <li><strong>Tous types de logements :</strong> maisons et appartements</li>
                <li><strong>Nécessite une pièce non chauffée</strong> de minimum 10 m² (buanderie, garage, cave)</li>
                <li><strong>Idéal pour remplacer un ballon électrique</strong> énergivore</li>
                <li><strong>Familles de 2 à 6 personnes</strong> (capacité 200 à 270 litres)</li>
                <li>Possibilité d'installation sur air extérieur via gaines</li>
              </ul>
            </div>

            {/* Caractéristiques techniques */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Caractéristiques Techniques</h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <ul className="space-y-4">
                  {[
                    'Capacité : 200 à 270 litres (2 à 6 personnes)',
                    'COP : 3 à 3,5 (3 kW produits pour 1 kW consommé)',
                    'Temps de chauffe : 6-8h en mode éco',
                    'Classe énergétique : A à A+',
                    'Température eau : jusqu\'à 62°C',
                    'Niveau sonore : 35-45 dB',
                    'Volume pièce minimum : 10 m² (20 m³)',
                    'Garantie : 2-3 ans (cuve garantie 5 ans)',
                  ].map((spec, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <Check className="text-[#2F5BA0] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="border-t-4 border-gray-200"></div>

      {/* BALLON SOLAIRE */}
      <section id="ballon-solaire" className="py-16 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Titre */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ballon Solaire (Chauffe-eau Solaire)</h2>
              <p className="text-xl text-gray-600">Eau chaude gratuite grâce au soleil - Écologie & Économies</p>
            </div>

            {/* Aides */}
            <div className="bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white rounded-2xl p-8 text-center mb-12 shadow-lg">
              <p className="text-lg mb-2 opacity-90">💰 Aides disponibles</p>
              <p className="text-3xl md:text-4xl font-bold">Jusqu'à 4 140€ (MaPrimeRénov' 4 000€ + CEE 140€)</p>
            </div>

            {/* Images */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                'https://heliophoros.com/web/image/1621/c.e.s.i.jpg',
                'https://www.dedietrich-thermique.fr/var/ddth/storage/images/_aliases/range-presentation_landscape/content/view/full/7526/50997-1-fre-FR/preparateur-solaire-uno.jpg',
                'https://www.reseau-proeco-energies.fr/img/realisations/1749-6710fc843b068.jpeg',
              ].map((img, index) => (
                <div key={index} className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all bg-white flex items-center justify-center" style={{minHeight: '300px'}}>
                  <img 
                    src={img} 
                    alt={`Ballon Solaire - ${index + 1}`}
                    className="w-full h-auto max-h-96 object-contain p-4"
                  />
                </div>
              ))}
            </div>

            {/* Qu'est-ce que */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Qu'est-ce qu'un chauffe-eau solaire (ballon solaire) ?</h3>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le chauffe-eau solaire individuel (CESI) est un système écologique qui utilise des panneaux solaires thermiques installés sur votre toiture pour capter l'énergie du soleil et chauffer l'eau stockée dans un ballon. Solution 100% renouvelable, il couvre 50 à 70% de vos besoins annuels en eau chaude selon votre région et l'ensoleillement.
              </p>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Un système d'appoint (électrique ou gaz) prend le relais les jours peu ensoleillés ou en hiver pour garantir votre confort en permanence. Investissement durable, le chauffe-eau solaire a une durée de vie de 20 à 25 ans.
              </p>
            </div>

            {/* Comment ça fonctionne */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comment fonctionne un chauffe-eau solaire ?</h3>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Le système se compose de 3 éléments principaux :
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-4 text-lg ml-4">
                <li><strong>Capteurs solaires thermiques :</strong> Installés sur votre toit, ils captent le rayonnement solaire et chauffent un fluide caloporteur</li>
                <li><strong>Ballon de stockage :</strong> Le fluide chauffé circule dans un échangeur thermique qui transfère la chaleur à l'eau sanitaire stockée dans le ballon (200 à 300 litres)</li>
                <li><strong>Système d'appoint :</strong> Une résistance électrique ou un échangeur gaz prend le relais automatiquement si nécessaire</li>
              </ol>
              <p className="mb-4 text-gray-700 leading-relaxed text-lg">
                Une régulation intelligente optimise le fonctionnement : dès que les capteurs sont plus chauds que l'eau du ballon, le circulateur se met en marche. Simple, efficace et totalement automatique !
              </p>
            </div>

            {/* Avantages et Inconvénients */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">✅ Avantages</h3>
                <div className="space-y-4">
                  {[
                    '☀️ Énergie 100% gratuite et renouvelable',
                    '💰 70% de vos besoins couverts',
                    '🔥 Aides jusqu\'à 4 140€',
                    '♻️ Zéro émission de CO₂',
                    '💵 200-300€ d\'économies/an',
                    '🏡 Valorisation immobilière',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="text-[#2D9A68] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">⚠️ Inconvénients</h3>
                <div className="space-y-4">
                  {[
                    '🏠 Nécessite toit orienté sud',
                    '📐 Pente de toit 30-45° requise',
                    '⚡ Appoint électrique nécessaire',
                    '💶 Coût initial élevé',
                    '🔧 Installation sur toiture complexe',
                    '⏱️ Retour sur investissement 8-10 ans',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-red-500 mt-1 flex-shrink-0">✕</span>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pour quels logements */}
            <div className="prose prose-lg max-w-none mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pour quel type de logement ?</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 text-lg">
                <li><strong>Maisons individuelles</strong> avec toiture exposée sud, sud-est ou sud-ouest</li>
                <li><strong>Surface de capteurs :</strong> 3 à 6 m² selon nombre d'occupants</li>
                <li><strong>Idéal pour familles</strong> : 4 à 6 personnes (consommation eau chaude importante)</li>
                <li><strong>Toitures inclinées</strong> (30 à 45°) ou toits terrasse</li>
                <li>Compatible avec tous types de toiture (tuiles, ardoises, bac acier)</li>
              </ul>
            </div>

            {/* Caractéristiques techniques */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Caractéristiques Techniques</h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <ul className="space-y-4">
                  {[
                    'Surface capteurs : 3 à 6 m² selon besoins',
                    'Capacité ballon : 200 à 300 litres',
                    'Rendement : 40-70% selon région et ensoleillement',
                    'Orientation optimale : Sud (±45°)',
                    'Inclinaison idéale : 30 à 45°',
                    'Appoint électrique ou gaz intégré',
                    'Durée de vie capteurs : 20-25 ans',
                    'Garantie capteurs : 10 ans',
                  ].map((spec, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <Check className="text-[#2F5BA0] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Besoin d'eau chaude économique ?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Nos experts vous conseillent sur la meilleure solution pour votre logement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/test-eligibilite"
              className="bg-[#E6A835] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#d49520] transition-all inline-flex items-center justify-center gap-2"
            >
              Testez votre éligibilité <ArrowRight />
            </Link>
            <Link
              to="/contact"
              className="bg-white text-[#2D9A68] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-2"
            >
              Demander un devis gratuit
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EauChaudeSanitairePage;
